import { useState, forwardRef, useImperativeHandle } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Input from "@/components/common/inputs/Input";
import WebIcon from "@/components/common/Web_Icons";
import Error from "@/components/common/alerts/Error";
import Success from "@/components/common/alerts/Success";
import { useLoader } from "@/hooks/LoaderProvider";
import {
  changePasswordAsync,
  selectError,
  logout,
} from "@/store/redux/slices/auth/auth_slice";

const ChangePassword = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const error = useSelector(selectError);
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoader();

  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [isErrorVisible, setIsErrorVisible] = useState(false);
  const [isSuccessVisible, setIsSuccessVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState({
    header: "",
    paragraph: "",
  });

  const handleChange = (e, field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
    setIsErrorVisible(false);
  };

  const validateForm = () => {
    if (
      !formData.currentPassword ||
      !formData.newPassword ||
      !formData.confirmPassword
    ) {
      setErrorMessage({
        header: "Missing Fields",
        paragraph: "Please fill in all password fields to continue.",
      });
      setIsErrorVisible(true);
      return false;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setErrorMessage({
        header: "Passwords Don't Match",
        paragraph:
          "Your new password and confirmation password do not match. Please try again.",
      });
      setIsErrorVisible(true);
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    showLoader(); // Show global loader before API call
    try {
      const result = await dispatch(
        changePasswordAsync({
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword,
        })
      );

      if (changePasswordAsync.fulfilled.match(result)) {
        setIsSuccessVisible(true);
        setFormData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      } else {
        const backendError = result?.payload;
        const paragraphMessage =
          backendError?.message ||
          backendError?.msg ||
          (typeof backendError === "string" ? backendError : error) ||
          "There was an error changing your password. Please try again.";

        setErrorMessage({
          header: "Password Change Failed",
          paragraph: paragraphMessage,
        });
        setIsErrorVisible(true);
      }
    } finally {
      hideLoader(); // Hide global loader after API call (success or failure)
    }
  };

  // Expose handleSubmit to parent component
  useImperativeHandle(ref, () => ({
    handleSubmit,
  }));

  const handleCloseError = () => {
    setIsErrorVisible(false);
  };

  const handleCloseSuccess = () => {
    setIsSuccessVisible(false);
    // Log out the user after successful password change
    setTimeout(() => {
      dispatch(logout());
    }, 1000);
  };

  // navigate to account recovery page
  const handleNavigate = () => {
    // First log the user out, then navigate to password recovery
    // dispatch(logout());
    // Add a small delay to ensure logout processes before navigation
    setTimeout(() => {
      navigate("/account-recovery/password-recovery");
    }, 100);
  };

  return (
    <div className="mt-4">
      <div>
        <h2 className="mb-2 text-bold">Current Password</h2>
        <p className="mb-4 text-regular text-lighterGrey">
          This is required in order to proceed with your password change. In
          case you forgot your password, you can recover it by{" "}
          <Link
            to="#"
            className="text-primary font-bold underline"
            onClick={handleNavigate}>
            clicking here
          </Link>
        </p>
        <Input
          label="PASSWORD"
          icon={<WebIcon icon="Lock" size="small" />}
          type="password"
          placeholder="Enter Password"
          value={formData.currentPassword}
          onChange={(e) => handleChange(e, "currentPassword")}
        />
      </div>

      <div className="mt-6">
        <h2 className="mb-2 text-bold">New Password</h2>
        <p className="mb-4 text-regular text-lighterGrey">
          You can set up a new password below. Ensure your password is safe and
          secured. And make use of alpha-numeric combination.
        </p>
        <div className="space-y-4">
          <Input
            label="NEW PASSWORD"
            icon={<WebIcon icon="Lock" size="small" />}
            type="password"
            placeholder="Enter Password"
            value={formData.newPassword}
            onChange={(e) => handleChange(e, "newPassword")}
          />
          <Input
            label="CONFIRM NEW PASSWORD"
            icon={<WebIcon icon="Lock" size="small" />}
            type="password"
            placeholder="Enter Password"
            value={formData.confirmPassword}
            onChange={(e) => handleChange(e, "confirmPassword")}
          />
        </div>
      </div>

      <Error
        isVisible={isErrorVisible}
        onClose={handleCloseError}
        header={errorMessage.header}
        paragraph={errorMessage.paragraph}
        buttonText="Try Again"
      />

      <Success
        isVisible={isSuccessVisible}
        onClose={handleCloseSuccess}
        header="Password Updated Successfully!"
        paragraph="Your password has been changed successfully. You will be logged out for security reasons."
        buttonText="Done"
      />
    </div>
  );
});

ChangePassword.displayName = "ChangePassword";

export default ChangePassword;
